import { DEFAULT_DSP_CONFIG, dspConfig, FEATURE_NAMES, saveDspConfig, } from "@beatbox/voice-core";
import { DEFAULT_GAME_SETTINGS, store } from "../app/state/store.js";
import { labelFor, registerCustomLabel, } from "../theme/naming.js";
import { iconFor, registerCustomIcon } from "../theme/icons.js";
import { escapeHtml } from "../game/util/escape.js";
import { exportLibrary, loadCustomPhonemes, loadHiddenPresets, loadPresetOverrides, parseLibrary, saveCustomPhonemes, saveHiddenPresets, savePresetOverrides, } from "../storage/phonemes.js";
import { createLibrary, deleteLibrary, duplicateLibrary, exportLibrary as exportPhonemeLibrary, getActiveLibraryId, importLibrary as importPhonemeLibrary, listLibraries, setActiveLibraryId, updateLibrary, } from "../storage/libraries.js";
const LANES = ["hiss", "airy", "pop", "hum"];
const EMOJI_PALETTE = [
    "🐍",
    "🌬️",
    "💥",
    "🎵",
    "🐝",
    "🔔",
    "👏",
    "💨",
    "🗣️",
    "👄",
    "😮",
    "😗",
    "🎤",
    "🎙️",
    "📢",
    "🔊",
    "🥁",
    "🎶",
    "⚡",
    "✨",
    "💢",
    "💦",
    "🌀",
    "🔥",
    "🐱",
    "🐶",
    "🦆",
    "🐸",
    "🦁",
    "🐮",
    "🐔",
    "🦉",
    "🅰️",
    "🅱️",
    "🆎",
    "🆑",
    "❓",
    "❗",
    "🔤",
    "🔡",
];
const SECTION_IDS = [
    "microphone",
    "detection",
    "visualizer",
    "library",
    "practice",
    "addsound",
    "traindata",
    "studio",
    "speaker",
    "classifier",
    "system",
];
const SECTION_TITLES = {
    microphone: "Microphone",
    detection: "Live detection",
    visualizer: "Signal visualizer",
    library: "Phoneme library",
    practice: "Practice each sound",
    addsound: "Add a new sound",
    traindata: "Training data",
    studio: "Studio (room & levels)",
    speaker: "Speaker tuning",
    classifier: "Classifier tuning",
    system: "System",
};
const LAYOUT_KEY = "diag-layout-v2";
function loadLayout() {
    try {
        const raw = localStorage.getItem(LAYOUT_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const order = (parsed.order ?? []).filter((id) => SECTION_IDS.includes(id));
            for (const id of SECTION_IDS) {
                if (!order.includes(id))
                    order.push(id);
            }
            return { order, collapsed: parsed.collapsed ?? {} };
        }
    }
    catch { }
    return { order: [...SECTION_IDS], collapsed: {} };
}
function saveLayout(layout) {
    try {
        localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
    }
    catch { }
}
const SILENCE = "silence";
const FEATURE_LABELS = {
    centroid: "Brightness (centroid)",
    noise: "Turbulence (noise)",
    voicing: "Voicing",
    energy: "Loudness (energy)",
    transient: "Onset (transient)",
    flatness: "Flatness (tonal↔noisy)",
    rolloff: "Rolloff (spectral)",
    tilt: "Tilt (high/low)",
    spread: "Spread (bandwidth)",
    crest: "Crest (peakiness)",
    flux: "Flux (spectral change)",
    zcr: "Zero-crossing rate",
    hnr: "Harmonicity (HNR)",
    pitchHz: "Pitch (Hz)",
    pitchConf: "Pitch clarity",
    pitchClass: "Pitch class",
};
const FEATURE_HUE = {
    centroid: 190,
    noise: 45,
    voicing: 280,
    energy: 140,
    transient: 0,
    flatness: 320,
    rolloff: 220,
    tilt: 90,
    spread: 200,
    crest: 25,
    flux: 350,
    zcr: 65,
    hnr: 160,
    pitchHz: 50,
    pitchConf: 170,
    pitchClass: 300,
};
const FEATURE_KEYS = [
    "centroid",
    "noise",
    "voicing",
    "energy",
    "transient",
    "flatness",
    "rolloff",
    "tilt",
    "spread",
    "crest",
    "flux",
    "zcr",
    "hnr",
    "pitchHz",
    "pitchConf",
    "pitchClass",
];
const PITCH_KEYS = [
    "pitchHz",
    "pitchConf",
    "pitchClass",
];
const RADAR_KEYS = FEATURE_KEYS.filter((k) => !PITCH_KEYS.includes(k));
const STUDIO_CONTROLS = [
    {
        key: "energyGate",
        label: "Silence gate",
        min: 0,
        max: 0.3,
        step: 0.005,
        hint: "Below this loudness, nothing is detected. Raise if noise triggers false hits.",
    },
    {
        key: "energyScale",
        label: "Loudness gain",
        min: 0.1,
        max: 10,
        step: 0.01,
        hint: "Scales input loudness. Raise for a quiet mic, lower for a hot mic.",
    },
    {
        key: "precisionSoftness",
        label: "Precision softness",
        min: 0.5,
        max: 10,
        step: 0.25,
        hint: "Larger is more forgiving — precision decays slowly with distance.",
    },
    {
        key: "frameHopMs",
        label: "Analysis packet (ms)",
        min: 5,
        max: 100,
        step: 1,
        hint: "How finely audio is chopped for classification. Smaller = finer " +
            "timing (better for pops) but more CPU; larger = coarser and steadier. " +
            "Changing this alters the feature space — recapture sounds afterward.",
    },
];
const SPEAKER_CONTROLS = [
    {
        key: "noiseBandLoHz",
        label: "Turbulence low (Hz)",
        min: 500,
        max: 4000,
        step: 50,
        hint: "Lower edge of the hiss/fricative band.",
    },
    {
        key: "noiseBandHiHz",
        label: "Turbulence high (Hz)",
        min: 4000,
        max: 12000,
        step: 100,
        hint: "Upper edge of the hiss/fricative band.",
    },
    {
        key: "voicingBandHiHz",
        label: "Voicing high (Hz)",
        min: 300,
        max: 2500,
        step: 50,
        hint: "Upper edge of the low/voicing band (hums).",
    },
    {
        key: "transientScale",
        label: "Onset sensitivity",
        min: 1,
        max: 12,
        step: 0.5,
        hint: "Sensitivity to sharp pops. Raise to make plosives easier to trigger.",
    },
    {
        key: "pitchMinHz",
        label: "Pitch min (Hz)",
        min: 20,
        max: 400,
        step: 5,
        hint: "Lowest fundamental searched for tonal sounds. Raise if low " +
            "hums lock an octave too low.",
    },
    {
        key: "pitchMaxHz",
        label: "Pitch max (Hz)",
        min: 400,
        max: 4000,
        step: 10,
        hint: "Highest fundamental searched for tonal sounds.",
    },
    {
        key: "pitchClarityGate",
        label: "Pitch clarity gate",
        min: 0.3,
        max: 0.95,
        step: 0.01,
        hint: "Min clarity to accept a pitch. Raise to reject jittery/noisy " +
            "estimates on quiet frames.",
    },
    {
        key: "voicingGateForPitch",
        label: "Pitch voicing gate",
        min: 0,
        max: 1,
        step: 0.02,
        hint: "Min voicing before pitch is attempted (tonal, not hiss).",
    },
    {
        key: "flatnessGateForPitch",
        label: "Pitch flatness gate",
        min: 0,
        max: 1,
        step: 0.02,
        hint: "Max flatness before pitch is attempted — a hissy/noisy frame " +
            "has no meaningful fundamental.",
    },
];
const DSP_CONTROLS = [...STUDIO_CONTROLS, ...SPEAKER_CONTROLS];
const CLASSIFIER_CONTROLS = [
    {
        key: "classifierC",
        label: "Regularization C",
        min: 0.1,
        max: 10,
        step: 0.1,
        hint: "SVM soft-margin strength. Higher fits the training data more " +
            "tightly (risk of overfitting); lower generalizes more.",
    },
    {
        key: "classifierGamma",
        label: "RBF gamma",
        min: 0.1,
        max: 10,
        step: 0.1,
        hint: "RBF kernel width. Higher makes decision boundaries tighter " +
            "around each example; lower makes them smoother.",
    },
    {
        key: "classifierRejectConfidence",
        label: "Reject confidence",
        min: 0,
        max: 1,
        step: 0.05,
        hint: "Minimum softmax confidence to accept a classification. Raise " +
            "to reject ambiguous frames.",
    },
    {
        key: "classifierTestFraction",
        label: "Test holdout",
        min: 0,
        max: 0.5,
        step: 0.01,
        hint: "Fraction of captured frames held out (stratified) to report " +
            "test accuracy after each retrain.",
    },
    {
        key: "classifierImpulseCBoost",
        label: "Impulse C boost",
        min: 1,
        max: 12,
        step: 0.5,
        hint: "Class-weight multiplier on C for percussive/impulse labels " +
            "(e.g. pop) which are under-represented per capture.",
    },
];
export class AcDiagnostics extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "last", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "built", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "holdingId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "history", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "vizRaf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "smooth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                centroid: 0,
                noise: 0,
                voicing: 0,
                energy: 0,
                transient: 0,
                flatness: 0,
                rolloff: 0,
                tilt: 0,
                spread: 0,
                crest: 0,
                flux: 0,
                zcr: 0,
                hnr: 0,
                pitchHz: 0,
                pitchConf: 0,
                pitchClass: -1,
            }
        });
        Object.defineProperty(this, "phase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "unsubSuggest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "layout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: loadLayout()
        });
        Object.defineProperty(this, "autoRetrain", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "trainDataSig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        // Canvas-space hit regions for radar axis labels (mouseover tooltips).
        Object.defineProperty(this, "radarHits", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "vizHover", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "customPhonemes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "hiddenPresets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "presetOverrides", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "newPhonemePercussive", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "newPhonemePitchVariant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "newPhonemeIcon", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        Object.defineProperty(this, "libraries", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "activeLibraryId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "default"
        });
        Object.defineProperty(this, "activeLibraryPhonemes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    connectedCallback() {
        this.style.position = "absolute";
        this.style.inset = "0";
        this.style.overflow = "auto";
        this.style.padding = "24px";
        this.style.boxSizing = "border-box";
        this.style.fontVariantNumeric = "tabular-nums";
        void this.loadCustom().then(() => this.render());
        this.unsubSuggest = store.suggestedEnergyScale.subscribe(() => this.decorateSuggestion(), false);
    }
    disconnectedCallback() {
        cancelAnimationFrame(this.vizRaf);
        this.vizRaf = 0;
        this.unsubSuggest?.();
        this.unsubSuggest = null;
        document.querySelector("[data-rows-popup]")?.remove();
        document.querySelector("[data-edit-popup]")?.remove();
    }
    refreshDspControls() {
        for (const c of DSP_CONTROLS) {
            const key = String(c.key);
            const inp = this.querySelector(`[data-dsp='${key}']`);
            const out = this.querySelector(`[data-dsp-val='${key}']`);
            const v = dspConfig[c.key];
            if (inp)
                inp.value = String(v);
            if (out)
                out.textContent = this.fmtDsp(c, v);
        }
    }
    update(snap) {
        this.last = snap;
        if (!this.built) {
            this.render();
        }
        else {
            this.patch();
        }
    }
    async loadCustom() {
        try {
            this.libraries = await listLibraries();
            this.activeLibraryId = await getActiveLibraryId();
            store.activeLibraryId.set(this.activeLibraryId);
            const active = this.libraries.find((l) => l.id === this.activeLibraryId);
            this.activeLibraryPhonemes = active ? active.phonemes : [];
        }
        catch (err) {
            console.warn("[ac-diagnostics] failed to load phoneme libraries", err);
            this.libraries = [];
            this.activeLibraryPhonemes = [];
        }
        try {
            this.customPhonemes = await loadCustomPhonemes();
        }
        catch (err) {
            console.warn("[ac-diagnostics] failed to load custom phonemes", err);
            this.customPhonemes = [];
        }
        try {
            this.hiddenPresets = await loadHiddenPresets();
        }
        catch (err) {
            console.warn("[ac-diagnostics] failed to load hidden presets", err);
            this.hiddenPresets = [];
        }
        try {
            this.presetOverrides = await loadPresetOverrides();
        }
        catch (err) {
            console.warn("[ac-diagnostics] failed to load preset overrides", err);
            this.presetOverrides = {};
        }
        for (const [id, ov] of Object.entries(this.presetOverrides)) {
            if (ov.label)
                registerCustomLabel(id, ov.label);
            if (ov.icon)
                registerCustomIcon(id, ov.icon);
        }
        for (const p of this.customPhonemes) {
            registerCustomLabel(p.id, p.label);
            registerCustomIcon(p.id, p.icon);
        }
    }
    practiceIds() {
        if (this.activeLibraryId === "default") {
            const visiblePresets = LANES.filter((id) => !this.hiddenPresets.includes(id));
            return [...visiblePresets, ...this.customPhonemes.map((p) => p.id)];
        }
        return this.activeLibraryPhonemes.map((p) => p.id);
    }
    buildLibraryBody() {
        const options = this.libraries
            .map((l) => `<option value="${escapeHtml(l.id)}" ` +
            `${l.id === this.activeLibraryId ? "selected" : ""}>` +
            `${escapeHtml(l.name)}${l.builtin ? " (built-in)" : ""}` +
            `${l.experimental ? " — experimental" : ""}` +
            `</option>`)
            .join("");
        const active = this.libraries.find((l) => l.id === this.activeLibraryId);
        const isBuiltin = active?.builtin ?? true;
        const trainedCount = active?.phonemes.filter((p) => p.trained).length ?? 0;
        const total = active?.phonemes.length ?? 0;
        const experimentalNote = active?.experimental
            ? `<div style="margin-top:10px;padding:8px 10px;border-radius:8px;
                   background:rgba(251,191,36,.12);border:1px solid #fbbf24;
                   font-size:11px;line-height:1.5;color:#fbbf24">
                 ⚠ Experimental library. These sounds differ mainly in pitch
                 trajectory, which the current per-frame classifier does not
                 model — expect weaker separation. Capture longer, steadier
                 holds and treat detection as best-effort.
               </div>`
            : "";
        return `
           <p style="opacity:.7;font-size:12px;margin:0 0 12px">
             Select which library of sounds is active. Switching a library
             changes the set of trainable phonemes and re-keys detection to that
             library's own training data — libraries never share corpora.
           </p>
           <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center">
             <select data-lib-select
                 style="pointer-events:auto;padding:8px 10px;border-radius:8px;
                 border:1px solid #2a2f4a;background:#12162a;color:#fff;
                 font-size:14px;min-width:200px">${options}</select>
             <span style="font-size:12px;opacity:.75">
               ${trainedCount}/${total} trained</span>
           </div>
          ${experimentalNote}
           <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:12px">
             <button data-lib-new
                 style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                 border-radius:8px;border:1px solid #4ade80;background:#1a1f3a;
                 color:#fff;font-size:13px;font-weight:600">New</button>
             <button data-lib-dup
                 style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                 border-radius:8px;border:1px solid #5b6cff;background:#1a1f3a;
                 color:#fff;font-size:13px;font-weight:600">Duplicate</button>
             <button data-lib-rename ${isBuiltin ? "disabled" : ""}
                 style="pointer-events:auto;cursor:${isBuiltin ? "default" : "pointer"};
                 padding:6px 12px;border-radius:8px;border:1px solid #5b6cff;
                 background:#1a1f3a;color:#fff;font-size:13px;font-weight:600;
                 opacity:${isBuiltin ? ".5" : "1"}">Rename</button>
             <button data-lib-delete ${isBuiltin ? "disabled" : ""}
                 style="pointer-events:auto;cursor:${isBuiltin ? "default" : "pointer"};
                 padding:6px 12px;border-radius:8px;border:1px solid #f87171;
                 background:#1a1f3a;color:#fff;font-size:13px;font-weight:600;
                 opacity:${isBuiltin ? ".5" : "1"}">Delete</button>
             <button data-lib-export2
                 style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                 border-radius:8px;border:1px solid #22d3ee;background:#1a1f3a;
                 color:#fff;font-size:13px;font-weight:600">Export JSON</button>
             <button data-lib-import2
                 style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                 border-radius:8px;border:1px solid #22d3ee;background:#1a1f3a;
                 color:#fff;font-size:13px;font-weight:600">Import JSON</button>
             <span data-lib2-status style="align-self:center;font-size:12px;
                 font-weight:600"></span>
           </div>`;
    }
    wireLibraryButtons() {
        const sel = this.querySelector("[data-lib-select]");
        sel?.addEventListener("change", () => {
            void this.selectLibrary(sel.value);
        });
        this.querySelector("[data-lib-new]")?.addEventListener("click", () => {
            const name = prompt("New library name?", "New Library");
            if (name == null)
                return;
            void (async () => {
                const lib = await createLibrary(name.trim() || "New Library");
                await this.selectLibrary(lib.id);
            })();
        });
        this.querySelector("[data-lib-dup]")?.addEventListener("click", () => {
            const active = this.libraries.find((l) => l.id === this.activeLibraryId);
            const name = prompt("Name for the duplicate?", `${active?.name ?? "Library"} copy`);
            if (name == null)
                return;
            void (async () => {
                const lib = await duplicateLibrary(this.activeLibraryId, name.trim() || "Library copy");
                await this.selectLibrary(lib.id);
            })();
        });
        this.querySelector("[data-lib-rename]")?.addEventListener("click", () => {
            const active = this.libraries.find((l) => l.id === this.activeLibraryId);
            if (!active || active.builtin)
                return;
            const name = prompt("Rename library to?", active.name);
            if (name == null)
                return;
            void (async () => {
                active.name = name.trim() || active.name;
                await updateLibrary(active);
                this.libraries = await listLibraries();
                this.built = false;
                this.render();
            })();
        });
        this.querySelector("[data-lib-delete]")?.addEventListener("click", () => {
            const active = this.libraries.find((l) => l.id === this.activeLibraryId);
            if (!active || active.builtin)
                return;
            if (!confirm(`Delete library "${active.name}"?`))
                return;
            void (async () => {
                await deleteLibrary(active.id);
                await this.selectLibrary("default");
            })();
        });
        this.querySelector("[data-lib-export2]")?.addEventListener("click", () => void this.exportPhonemeLibraryToClipboard());
        this.querySelector("[data-lib-import2]")?.addEventListener("click", () => void this.importPhonemeLibraryFromClipboard());
    }
    async selectLibrary(id) {
        await setActiveLibraryId(id);
        this.activeLibraryId = id;
        await this.loadCustom();
        store.activeLibraryId.set(id);
        this.dispatchEvent(new CustomEvent("library-changed", {
            detail: { libraryId: id },
            bubbles: true,
        }));
        console.info(`[ac-diagnostics] active library switched to '${id}'`);
        this.built = false;
        this.render();
    }
    async exportPhonemeLibraryToClipboard() {
        try {
            const json = await exportPhonemeLibrary(this.activeLibraryId);
            await navigator.clipboard.writeText(json);
            this.flashLib2Status("Copied library JSON ✓", "#4ade80");
        }
        catch (err) {
            console.warn("[ac-diagnostics] library export failed", err);
            this.flashLib2Status("Export failed — see console", "#f87171");
        }
    }
    async importPhonemeLibraryFromClipboard() {
        let text;
        try {
            text = await navigator.clipboard.readText();
        }
        catch (err) {
            console.warn("[ac-diagnostics] clipboard read failed", err);
            this.flashLib2Status("Clipboard read blocked", "#f87171");
            return;
        }
        try {
            const lib = await importPhonemeLibrary(text);
            this.flashLib2Status(`Imported "${lib.name}" ✓`, "#4ade80");
            await this.selectLibrary(lib.id);
        }
        catch (err) {
            console.warn("[ac-diagnostics] library import failed", err);
            this.flashLib2Status("Invalid library JSON", "#f87171");
        }
    }
    flashLib2Status(msg, color) {
        const el = this.querySelector("[data-lib2-status]");
        if (!el)
            return;
        el.textContent = msg;
        el.style.color = color;
        window.setTimeout(() => {
            if (el.textContent === msg)
                el.textContent = "";
        }, 3000);
    }
    isPercussive(id) {
        // Prefer the active-library definition when we're on a non-default
        // library.
        if (this.activeLibraryId !== "default") {
            const def = this.activeLibraryPhonemes.find((p) => p.id === id);
            if (def)
                return def.percussive;
        }
        if (LANES.includes(id)) {
            return this.presetOverrides[id]?.percussive ?? id === "pop";
        }
        const custom = this.customPhonemes.find((p) => p.id === id);
        return custom?.percussive ?? false;
    }
    async addCustomPhoneme(id, label, icon, percussive, pitchInvariant) {
        const clean = id
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9_-]/g, "");
        if (!clean) {
            console.warn("[ac-diagnostics] invalid phoneme id");
            return;
        }
        if (LANES.includes(clean) ||
            this.customPhonemes.some((p) => p.id === clean)) {
            console.warn(`[ac-diagnostics] phoneme '${clean}' already exists`);
            return;
        }
        const phoneme = {
            id: clean,
            label: label.trim() || clean,
            icon: icon.trim() || "❓",
            percussive,
            pitchInvariant,
        };
        this.customPhonemes.push(phoneme);
        registerCustomLabel(phoneme.id, phoneme.label);
        registerCustomIcon(phoneme.id, phoneme.icon);
        await saveCustomPhonemes(this.customPhonemes);
        console.info(`[ac-diagnostics] added custom phoneme '${phoneme.id}'`);
        this.newPhonemeIcon = "";
        this.newPhonemePercussive = false;
        this.newPhonemePitchVariant = false;
        this.built = false;
        this.render();
    }
    async removeCustomPhoneme(id) {
        this.customPhonemes = this.customPhonemes.filter((p) => p.id !== id);
        await saveCustomPhonemes(this.customPhonemes);
        this.dispatchEvent(new CustomEvent("train-clear-label", {
            detail: { label: id },
            bubbles: true,
        }));
        console.info(`[ac-diagnostics] removed custom phoneme '${id}'`);
        this.built = false;
        this.render();
    }
    async removePresetPhoneme(id) {
        if (!this.hiddenPresets.includes(id)) {
            this.hiddenPresets.push(id);
            await saveHiddenPresets(this.hiddenPresets);
        }
        this.dispatchEvent(new CustomEvent("train-clear-label", {
            detail: { label: id },
            bubbles: true,
        }));
        console.info(`[ac-diagnostics] hid preset phoneme '${id}'`);
        this.built = false;
        this.render();
    }
    async restorePresets() {
        this.hiddenPresets = [];
        await saveHiddenPresets(this.hiddenPresets);
        console.info("[ac-diagnostics] restored all preset phonemes");
        this.built = false;
        this.render();
    }
    phonemeProps(id, isPreset) {
        const theme = "child";
        if (this.activeLibraryId !== "default") {
            const def = this.activeLibraryPhonemes.find((p) => p.id === id);
            if (def) {
                return {
                    label: def.label,
                    icon: def.icon,
                    percussive: def.percussive,
                    pitchInvariant: def.pitchInvariant,
                    description: def.description,
                    pronunciation: def.pronunciation,
                };
            }
        }
        if (isPreset) {
            const ov = this.presetOverrides[id] ?? {};
            return {
                label: ov.label ?? labelFor(id, theme),
                icon: ov.icon ?? iconFor(id, theme),
                percussive: ov.percussive ?? id === "pop",
                pitchInvariant: ov.pitchInvariant ?? true,
            };
        }
        const custom = this.customPhonemes.find((p) => p.id === id);
        return {
            label: custom?.label ?? id,
            icon: custom?.icon ?? "❓",
            percussive: custom?.percussive ?? false,
            pitchInvariant: custom?.pitchInvariant ?? true,
        };
    }
    async saveEditedPhoneme(id, isPreset, props) {
        if (isPreset) {
            this.presetOverrides[id] = {
                label: props.label,
                icon: props.icon,
                percussive: props.percussive,
                pitchInvariant: props.pitchInvariant,
            };
            registerCustomLabel(id, props.label);
            registerCustomIcon(id, props.icon);
            await savePresetOverrides(this.presetOverrides);
            console.info(`[ac-diagnostics] saved preset override '${id}'`);
        }
        else {
            const idx = this.customPhonemes.findIndex((p) => p.id === id);
            if (idx >= 0) {
                this.customPhonemes[idx] = {
                    ...this.customPhonemes[idx],
                    label: props.label,
                    icon: props.icon,
                    percussive: props.percussive,
                    pitchInvariant: props.pitchInvariant,
                };
                registerCustomLabel(id, props.label);
                registerCustomIcon(id, props.icon);
                await saveCustomPhonemes(this.customPhonemes);
                console.info(`[ac-diagnostics] saved custom phoneme '${id}'`);
            }
        }
        this.built = false;
        this.render();
    }
    showEditPopup(id, isPreset) {
        document.querySelector("[data-edit-popup]")?.remove();
        const p = this.phonemeProps(id, isPreset);
        const safeLabel = escapeHtml(p.label);
        const safeIcon = escapeHtml(p.icon);
        const safeId = escapeHtml(id);
        const overlay = document.createElement("div");
        overlay.setAttribute("data-edit-popup", id);
        overlay.style.cssText =
            "position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,.6);" +
                "display:flex;align-items:center;justify-content:center;" +
                "pointer-events:auto";
        overlay.innerHTML = `
          <div style="background:#0d1024;border:1px solid #2a2f4a;
              border-radius:14px;width:min(92vw,420px);color:#fff;padding:20px">
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">
              <span style="flex:1;font-size:16px;font-weight:700">
                Edit ${isPreset ? "preset" : "sound"} "${safeId}"</span>
              <button data-edit-close
                  style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                  border-radius:8px;border:1px solid #5b6cff;background:#1a1f3a;
                  color:#fff;font-size:13px;font-weight:600">Close</button>
            </div>
            <div style="display:flex;gap:8px;align-items:center;margin-bottom:10px">
              <button type="button" data-edit-icon-toggle
                  style="pointer-events:auto;cursor:pointer;width:52px;height:40px;
                  text-align:center;padding:8px;border-radius:8px;
                  border:1px solid #2a2f4a;background:#12162a;color:#fff;
                  font-size:18px">${safeIcon || "🔤"}</button>
              <input data-edit-icon type="hidden" value="${safeIcon}"/>
              <input data-edit-label type="text" value="${safeLabel}"
                  placeholder="label"
                  style="pointer-events:auto;flex:1;padding:8px;border-radius:8px;
                  border:1px solid #2a2f4a;background:#12162a;color:#fff;font-size:14px"/>
            </div>
            <div data-edit-icon-picker style="display:none;flex-wrap:wrap;gap:6px;
                margin-bottom:10px;padding:8px;border-radius:8px;
                background:#0d1024;border:1px solid #2a2f4a">
              ${EMOJI_PALETTE.map((e) => `<button type="button" data-edit-pick-emoji="${e}"
                       style="pointer-events:auto;cursor:pointer;width:34px;
                       height:34px;border-radius:8px;border:1px solid #2a2f4a;
                       background:#12162a;font-size:18px">${e}</button>`).join("")}
            </div>
            <label style="display:flex;align-items:center;gap:8px;font-size:13px;
                margin:8px 0;pointer-events:auto;cursor:pointer">
              <input type="checkbox" data-edit-percussive
                  ${p.percussive ? "checked" : ""}
                  style="pointer-events:auto;cursor:pointer"/>
              Percussive (tap/onset)
            </label>
            <label style="display:flex;align-items:center;gap:8px;font-size:13px;
                margin:8px 0;pointer-events:auto;cursor:pointer">
              <input type="checkbox" data-edit-pitchvariant
                  ${!p.pitchInvariant ? "checked" : ""}
                  style="pointer-events:auto;cursor:pointer"/>
              Use pitch to discriminate (speaker-specific)
            </label>
            <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:14px">
              <button data-edit-save
                  style="pointer-events:auto;cursor:pointer;padding:8px 16px;
                  border-radius:10px;border:1px solid #4ade80;background:#1a1f3a;
                  color:#fff;font-size:14px;font-weight:700">Save</button>
            </div>
          </div>`;
        const close = () => overlay.remove();
        overlay.addEventListener("click", (e) => {
            if (e.target === overlay)
                close();
        });
        overlay
            .querySelector("[data-edit-close]")
            ?.addEventListener("click", close);
        const iconToggle = overlay.querySelector("[data-edit-icon-toggle]");
        const iconPicker = overlay.querySelector("[data-edit-icon-picker]");
        const iconInput = overlay.querySelector("[data-edit-icon]");
        iconToggle?.addEventListener("click", (e) => {
            e.preventDefault();
            if (iconPicker) {
                iconPicker.style.display =
                    iconPicker.style.display === "none" ? "flex" : "none";
            }
        });
        overlay
            .querySelectorAll("[data-edit-pick-emoji]")
            .forEach((btn) => {
            const emoji = btn.getAttribute("data-edit-pick-emoji");
            if (!emoji)
                return;
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                if (iconInput)
                    iconInput.value = emoji;
                if (iconToggle)
                    iconToggle.textContent = emoji;
                if (iconPicker)
                    iconPicker.style.display = "none";
            });
        });
        overlay.querySelector("[data-edit-save]")?.addEventListener("click", () => {
            const label = overlay.querySelector("[data-edit-label]")?.value ??
                p.label;
            const icon = overlay.querySelector("[data-edit-icon]")?.value ??
                p.icon;
            const percussive = overlay.querySelector("[data-edit-percussive]")
                ?.checked ?? p.percussive;
            const usePitch = overlay.querySelector("[data-edit-pitchvariant]")
                ?.checked ?? !p.pitchInvariant;
            void this.saveEditedPhoneme(id, isPreset, {
                label: label.trim() || id,
                icon: icon.trim() || "❓",
                percussive,
                pitchInvariant: !usePitch,
            });
            close();
        });
        document.body.appendChild(overlay);
    }
    async exportLibraryToClipboard() {
        const json = exportLibrary(this.customPhonemes, this.hiddenPresets, this.presetOverrides);
        try {
            await navigator.clipboard.writeText(json);
            console.info("[ac-diagnostics] copied phoneme library to clipboard");
            this.flashLibraryStatus("Copied library to clipboard ✓", "#4ade80");
        }
        catch (err) {
            console.warn("[ac-diagnostics] clipboard write failed", err);
            this.flashLibraryStatus("Clipboard blocked — see console for JSON", "#f87171");
            console.info("[ac-diagnostics] phoneme library JSON:\n" + json);
        }
    }
    async importLibraryFromClipboard() {
        let text;
        try {
            text = await navigator.clipboard.readText();
        }
        catch (err) {
            console.warn("[ac-diagnostics] clipboard read failed", err);
            this.flashLibraryStatus("Clipboard read blocked by browser", "#f87171");
            return;
        }
        try {
            const lib = parseLibrary(text);
            const byId = new Map();
            for (const p of this.customPhonemes)
                byId.set(p.id, p);
            for (const p of lib.custom) {
                const clean = p.id
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9_-]/g, "");
                if (!clean || LANES.includes(clean))
                    continue;
                byId.set(clean, {
                    id: clean,
                    label: p.label?.trim() || clean,
                    icon: p.icon?.trim() || "❓",
                    percussive: !!p.percussive,
                    pitchInvariant: p.pitchInvariant ?? true,
                });
            }
            this.customPhonemes = [...byId.values()];
            const hidden = new Set(this.hiddenPresets);
            for (const id of lib.hiddenPresets) {
                if (LANES.includes(id))
                    hidden.add(id);
            }
            this.hiddenPresets = [...hidden];
            if (lib.presetOverrides) {
                this.presetOverrides = {
                    ...this.presetOverrides,
                    ...lib.presetOverrides,
                };
            }
            for (const p of this.customPhonemes) {
                registerCustomLabel(p.id, p.label);
                registerCustomIcon(p.id, p.icon);
            }
            for (const [id, ov] of Object.entries(this.presetOverrides)) {
                if (ov.label)
                    registerCustomLabel(id, ov.label);
                if (ov.icon)
                    registerCustomIcon(id, ov.icon);
            }
            await saveCustomPhonemes(this.customPhonemes);
            await saveHiddenPresets(this.hiddenPresets);
            await savePresetOverrides(this.presetOverrides);
            console.info(`[ac-diagnostics] imported library: ` +
                `${lib.custom.length} custom, ` +
                `${lib.hiddenPresets.length} hidden`);
            this.flashLibraryStatus("Imported library ✓", "#4ade80");
            this.built = false;
            this.render();
        }
        catch (err) {
            console.warn("[ac-diagnostics] failed to parse library JSON", err);
            this.flashLibraryStatus("Invalid JSON on clipboard", "#f87171");
        }
    }
    flashLibraryStatus(msg, color) {
        const el = this.querySelector("[data-lib-status]");
        if (!el)
            return;
        el.textContent = msg;
        el.style.color = color;
        window.setTimeout(() => {
            if (el.textContent === msg)
                el.textContent = "";
        }, 3000);
    }
    classifierPatchFromSettings() {
        const s = store.gameSettings.get();
        return {
            C: s.classifierC,
            gamma: s.classifierGamma,
            rejectConfidence: s.classifierRejectConfidence,
            testFraction: s.classifierTestFraction,
            impulseCBoost: s.classifierImpulseCBoost,
        };
    }
    fmtDsp(c, v) {
        if (String(c.key).endsWith("Hz"))
            return `${Math.round(v)} Hz`;
        if (c.key === "frameHopMs")
            return `${Math.round(v)} ms`;
        return v.toFixed(c.step < 0.05 ? 3 : 2);
    }
    fmtClassifier(c, v) {
        if (c.key === "classifierRejectConfidence" ||
            c.key === "classifierTestFraction") {
            return `${Math.round(v * 100)}%`;
        }
        if (c.key === "classifierImpulseCBoost")
            return `${v.toFixed(1)}x`;
        return v.toFixed(c.step < 0.05 ? 2 : 1);
    }
    classifierRow(c) {
        const v = store.gameSettings.get()[c.key];
        return `<div style="margin:10px 0">
             <div style="display:flex;justify-content:space-between;font-size:12px;opacity:.85">
               <span>${c.label}</span>
               <span data-clf-val="${c.key}">${this.fmtClassifier(c, v)}</span>
             </div>
             <input type="range" data-clf="${c.key}" min="${c.min}" max="${c.max}"
                 step="${c.step}" value="${v}"
                 style="pointer-events:auto;width:100%;margin-top:4px"/>
             <div style="opacity:.55;font-size:11px;margin-top:2px">${c.hint}</div>
           </div>`;
    }
    pitchClassName(pc) {
        if (pc < 0)
            return "—";
        const idx = Math.round(pc) % 12;
        return AcDiagnostics.NOTE_NAMES[idx] ?? "—";
    }
    pitchReadoutInner(f) {
        const hz = f?.pitchHz ?? 0;
        const conf = f?.pitchConf ?? 0;
        const pc = f?.pitchClass ?? -1;
        const voiced = hz > 0;
        const hzTxt = voiced ? `${Math.round(hz)} Hz` : "—";
        const noteTxt = voiced ? this.pitchClassName(pc) : "—";
        const confTxt = voiced ? `${(conf * 100) | 0}%` : "—";
        const stat = (label, val, color) => `<span style="display:inline-flex;flex-direction:column">
               <span style="font-size:11px;opacity:.6;text-transform:uppercase;
                   letter-spacing:.04em">${label}</span>
               <span style="font-weight:700;color:${color}">${val}</span>
             </span>`;
        return (stat("Pitch", hzTxt, voiced ? "#fbbf24" : "#8891b5") +
            stat("Note", noteTxt, voiced ? "#22d3ee" : "#8891b5") +
            stat("Clarity", confTxt, voiced ? "#4ade80" : "#8891b5"));
    }
    meterValue(k, v) {
        if (k === "pitchHz") {
            if (!(v > 0))
                return 0;
            const lo = Math.log2(dspConfig.pitchMinHz);
            const hi = Math.log2(dspConfig.pitchMaxHz);
            const t = (Math.log2(v) - lo) / (hi - lo || 1);
            return t < 0 ? 0 : t > 1 ? 1 : t;
        }
        if (k === "pitchClass")
            return v < 0 ? 0 : v / 12;
        return Math.max(0, Math.min(1, v));
    }
    columnStats(rows) {
        const dim = FEATURE_NAMES.length;
        const out = [];
        for (let d = 0; d < dim; d++) {
            let sum = 0;
            let min = Infinity;
            let max = -Infinity;
            for (const r of rows) {
                const v = r[d] ?? 0;
                sum += v;
                if (v < min)
                    min = v;
                if (v > max)
                    max = v;
            }
            const mean = rows.length ? sum / rows.length : 0;
            let varAcc = 0;
            for (const r of rows) {
                const v = (r[d] ?? 0) - mean;
                varAcc += v * v;
            }
            const std = rows.length ? Math.sqrt(varAcc / rows.length) : 0;
            out.push({
                mean,
                std,
                min: rows.length ? min : 0,
                max: rows.length ? max : 0,
            });
        }
        return out;
    }
    buildTrainDataBody(s) {
        const data = s?.trainData ?? {};
        const labels = Object.keys(data).sort();
        const controls = `<div style="display:flex;flex-wrap:wrap;align-items:center;
            gap:10px;margin:0 0 12px">
            <label style="display:inline-flex;align-items:center;gap:6px;
                font-size:12px;pointer-events:auto;cursor:pointer">
              <input type="checkbox" data-autoretrain ${this.autoRetrain ? "checked" : ""}
                  style="pointer-events:auto;cursor:pointer"/>
              Auto-retrain
            </label>
            <button data-retrain-now
                style="pointer-events:auto;cursor:pointer;padding:6px 10px;
                border-radius:8px;border:1px solid #4ade80;background:#1a1f3a;
                color:#fff;font-size:12px;font-weight:600">Retrain now</button>
           <button data-download-tsv
               style="pointer-events:auto;cursor:pointer;padding:6px 10px;
               border-radius:8px;border:1px solid #22d3ee;background:#1a1f3a;
               color:#fff;font-size:12px;font-weight:600">Download TSV</button>
            <button data-clear-all
                style="pointer-events:auto;cursor:pointer;padding:6px 10px;
                border-radius:8px;border:1px solid #f87171;background:#1a1f3a;
                color:#fff;font-size:12px;font-weight:600">Clear all</button>
          </div>`;
        if (labels.length === 0) {
            return `${controls}<p style="opacity:.7;font-size:12px;margin:0">
                No labeled training data captured yet. Calibrate a sound in
                "Practice each sound" to begin accumulating rows.
              </p>`;
        }
        const header = `<div style="display:flex;font-size:11px;opacity:.6;
            text-transform:uppercase;letter-spacing:.04em;padding:0 4px 4px">
            <span style="flex:0 0 90px">Label</span>
            <span style="flex:0 0 70px">Rows</span>
            <span style="flex:1">Per-feature μ (σ)</span>
            <span style="flex:0 0 150px;text-align:right">Actions</span>
          </div>`;
        const rows = labels
            .map((label) => {
            const grid = data[label] ?? [];
            const stats = this.columnStats(grid);
            const safeLabel = escapeHtml(label);
            const statTxt = FEATURE_NAMES.map((n, i) => `<span style="display:inline-block;margin-right:10px">
                       ${n} <b>${stats[i].mean.toFixed(2)}</b>
                       <span style="opacity:.6">(${stats[i].std.toFixed(2)})</span>
                     </span>`).join("");
            return `<div style="display:flex;align-items:center;
                padding:8px 4px;border-top:1px solid #2a2f4a">
                <span style="flex:0 0 90px;font-weight:600">${safeLabel}</span>
                <span style="flex:0 0 70px">${grid.length}</span>
                <span style="flex:1;font-size:11px;line-height:1.7">${statTxt}</span>
                <span style="flex:0 0 150px;text-align:right;display:flex;
                    gap:6px;justify-content:flex-end">
                  <button data-view-rows="${safeLabel}"
                      style="pointer-events:auto;cursor:pointer;padding:4px 8px;
                      border-radius:8px;border:1px solid #5b6cff;background:#1a1f3a;
                      color:#fff;font-size:12px;font-weight:600">View</button>
                  <button data-clear-label="${safeLabel}"
                      style="pointer-events:auto;cursor:pointer;padding:4px 8px;
                      border-radius:8px;border:1px solid #f87171;background:#1a1f3a;
                      color:#fff;font-size:12px;font-weight:600">Clear</button>
                </span>
              </div>`;
        })
            .join("");
        return `${controls}<p style="opacity:.7;font-size:12px;margin:0 0 8px">
            Accumulated labeled training data. Each row is a captured feature
            vector in ${FEATURE_NAMES.join(", ")} order.
          </p>
          ${header}
          ${rows}`;
    }
    /** Wire the per-label "View" buttons to open the raw-rows popup. */
    wireTrainDataButtons() {
        this.querySelectorAll("[data-view-rows]").forEach((btn) => {
            const label = btn.getAttribute("data-view-rows");
            if (!label)
                return;
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                this.showRowsPopup(label);
            });
        });
        // Per-label clear buttons.
        this.querySelectorAll("[data-clear-label]").forEach((btn) => {
            const label = btn.getAttribute("data-clear-label");
            if (!label)
                return;
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                this.dispatchEvent(new CustomEvent("train-clear-label", {
                    detail: { label },
                    bubbles: true,
                }));
            });
        });
        // Global "clear all" button.
        this.querySelector("[data-clear-all]")?.addEventListener("click", (e) => {
            e.preventDefault();
            this.dispatchEvent(new CustomEvent("train-clear-all", { bubbles: true }));
        });
        // Explicit "retrain now" button.
        this.querySelector("[data-retrain-now]")?.addEventListener("click", (e) => {
            e.preventDefault();
            this.dispatchEvent(new CustomEvent("train-retrain", { bubbles: true }));
        });
        // Download the entire captured corpus as a TSV (header + all labels).
        this.querySelector("[data-download-tsv]")?.addEventListener("click", (e) => {
            e.preventDefault();
            this.downloadTrainingTsv();
        });
        // Auto-retrain checkbox (defaults checked).
        const autoBox = this.querySelector("[data-autoretrain]");
        if (autoBox) {
            autoBox.checked = this.autoRetrain;
            autoBox.addEventListener("change", () => {
                this.autoRetrain = autoBox.checked;
                this.dispatchEvent(new CustomEvent("train-autoretrain", {
                    detail: { enabled: autoBox.checked },
                    bubbles: true,
                }));
            });
        }
    }
    /** Render a modal-style popup listing every captured row for a label. */
    showRowsPopup(label) {
        // Remove any existing popup first.
        document.querySelector("[data-rows-popup]")?.remove();
        const rows = this.last?.trainData?.[label] ?? [];
        const safeLabel = escapeHtml(label);
        const overlay = document.createElement("div");
        overlay.setAttribute("data-rows-popup", label);
        overlay.style.cssText =
            "position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,.6);" +
                "display:flex;align-items:center;justify-content:center;" +
                "pointer-events:auto";
        const headCols = FEATURE_NAMES.map((n) => `<th style="text-align:right;padding:4px 10px;position:sticky;
                    top:0;background:#12162a">${n}</th>`).join("");
        const bodyRows = rows
            .map((r, i) => {
            const cells = FEATURE_NAMES.map((_n, d) => `<td style="text-align:right;padding:3px 10px">
                           ${(r[d] ?? 0).toFixed(4)}</td>`).join("");
            return `<tr style="border-top:1px solid #2a2f4a">
                    <td style="text-align:right;padding:3px 10px;opacity:.5">
                      ${i}</td>${cells}</tr>`;
        })
            .join("");
        overlay.innerHTML = `
          <div style="background:#0d1024;border:1px solid #2a2f4a;
              border-radius:14px;max-width:720px;width:90%;max-height:80vh;
              display:flex;flex-direction:column;color:#fff">
            <div style="display:flex;align-items:center;gap:10px;
                padding:14px 18px;border-bottom:1px solid #2a2f4a">
              <span style="flex:1;font-size:16px;font-weight:700">
                Rows for "${safeLabel}" (${rows.length})</span>
              <button data-rows-close
                  style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                  border-radius:8px;border:1px solid #5b6cff;background:#1a1f3a;
                  color:#fff;font-size:13px;font-weight:600">Close</button>
            </div>
            <div style="overflow:auto;padding:0 18px 18px">
              <table style="width:100%;border-collapse:collapse;font-size:12px;
                  font-variant-numeric:tabular-nums">
                <thead><tr>
                  <th style="text-align:right;padding:4px 10px;position:sticky;
                      top:0;background:#12162a">#</th>${headCols}
                </tr></thead>
                <tbody>${bodyRows}</tbody>
              </table>
            </div>
          </div>`;
        const close = () => overlay.remove();
        overlay.addEventListener("click", (e) => {
            if (e.target === overlay)
                close();
        });
        overlay
            .querySelector("[data-rows-close]")
            ?.addEventListener("click", close);
        // Append to <body> so the panel's overflow/position (and the periodic
        // patch() that rebuilds the training-data body) cannot clip or remove
        // the popup out from under the user.
        document.body.appendChild(overlay);
    }
    /**
     * Serialize the entire captured corpus (all labels) as a TSV string with a
     * header row: `label` followed by every FEATURE_NAMES column. One row per
     * captured feature vector. Values are the raw dense array in
     * FEATURE_NAMES order.
     */
    buildTrainingTsv() {
        const data = this.last?.trainData ?? {};
        const header = ["label", ...FEATURE_NAMES].join("\t");
        const lines = [header];
        for (const label of Object.keys(data).sort()) {
            for (const row of data[label] ?? []) {
                const cells = FEATURE_NAMES.map((_n, d) => (row[d] ?? 0).toString());
                lines.push([label, ...cells].join("\t"));
            }
        }
        return lines.join("\n") + "\n";
    }
    downloadTrainingTsv() {
        const data = this.last?.trainData ?? {};
        const total = Object.values(data).reduce((a, rows) => a + rows.length, 0);
        if (total === 0) {
            console.warn("[ac-diagnostics] no training data to download");
            return;
        }
        const tsv = this.buildTrainingTsv();
        const blob = new Blob([tsv], { type: "text/tab-separated-values" });
        const url = URL.createObjectURL(blob);
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        const a = document.createElement("a");
        a.href = url;
        a.download = `training-data-${stamp}.tsv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        console.info(`[ac-diagnostics] downloaded ${total} training rows across ` +
            `${Object.keys(data).length} label(s) as TSV`);
    }
    sectionShell(id, bodyHtml) {
        const collapsed = this.layout.collapsed[id] ?? false;
        return `<section data-section="${id}"
            style="background:rgba(18,22,42,.6);border:1px solid #2a2f4a;
            border-radius:14px;padding:0;margin-bottom:16px;overflow:hidden">
          <div data-section-head="${id}" draggable="true"
              style="display:flex;align-items:center;gap:10px;padding:12px 16px;
              cursor:pointer;pointer-events:auto;user-select:none;
              background:rgba(255,255,255,.02)">
            <span data-drag-handle title="Drag to reorder"
                style="cursor:grab;opacity:.6;font-size:16px;line-height:1">⠿</span>
            <span style="flex:1;font-size:16px;font-weight:700">${SECTION_TITLES[id]}</span>
            <span data-caret="${id}" style="opacity:.7;font-size:13px;
                transition:transform .15s ease;transform:rotate(${collapsed ? "-90deg" : "0deg"})">▾</span>
          </div>
          <div data-section-body="${id}"
              style="padding:0 16px 16px;display:${collapsed ? "none" : "block"}">
            ${bodyHtml}
          </div>
        </section>`;
    }
    dspRow(c) {
        const v = dspConfig[c.key];
        const key = String(c.key);
        const suggestBadge = c.key === "energyScale"
            ? `<span data-dsp-suggest="energyScale"
                        style="margin-left:8px;font-size:11px;opacity:.85;
                        color:#4ade80;pointer-events:auto;cursor:pointer;
                        display:none"></span>`
            : "";
        return `<div style="margin:10px 0">
             <div style="display:flex;justify-content:space-between;font-size:12px;opacity:.85">
               <span>${c.label}${suggestBadge}</span>
              <span data-dsp-val="${key}">${this.fmtDsp(c, v)}</span>
             </div>
            <input type="range" data-dsp="${key}" min="${c.min}" max="${c.max}"
                 step="${c.step}" value="${v}"
                 style="pointer-events:auto;width:100%;margin-top:4px"/>
             <div style="opacity:.55;font-size:11px;margin-top:2px">${c.hint}</div>
           </div>`;
    }
    render() {
        // Build the DOM once; after that we patch values in place so an
        // in-progress press-and-hold button is never torn down (which would
        // eat the pointerup and wedge calibration "on" forever).
        if (this.built) {
            this.patch();
            return;
        }
        const theme = "child";
        const s = this.last;
        const detected = s?.clusterId ?? null;
        const detectedLabel = detected ? labelFor(detected, theme) : "—";
        const detectedGlyph = detected ? iconFor(detected, theme) : "•";
        const micOk = (s?.level ?? 0) > 0.02;
        const micStatus = micOk
            ? `<span style="color:#4ade80">receiving audio</span>`
            : `<span style="color:#fbbf24">quiet — try speaking / making a sound</span>`;
        const featureBars = s
            ? Object.keys(FEATURE_LABELS)
                .map((k) => `<div style="margin:6px 0">
                             <div style="display:flex;justify-content:space-between;font-size:12px;opacity:.7">
                               <span>${FEATURE_LABELS[k]}</span>
                             </div>
                             <div style="height:8px;border-radius:4px;background:rgba(255,255,255,.12);overflow:hidden">
                                <div data-meter="${String(k)}" style="height:100%;width:${Math.round(this.meterValue(k, s.features[k] ?? 0) * 100)}%;background:#22d3ee;transition:width .08s linear"></div>
                             </div>
                           </div>`)
                .join("")
            : "";
        const practice = this.practiceIds()
            .map((id) => {
            const glyph = iconFor(id, theme);
            const label = labelFor(id, theme);
            const safeGlyph = escapeHtml(glyph);
            const safeLabel = escapeHtml(label);
            const safeId = escapeHtml(id);
            const held = s?.holdMs?.[id] ?? 0;
            const active = detected === id;
            const secs = (held / 1000).toFixed(1);
            const count = s?.hits?.[id] ?? 0;
            const isTap = this.isPercussive(id);
            const metric = isTap
                ? `${count} pop${count === 1 ? "" : "s"}`
                : `held ${secs}s`;
            const tuned = s?.tunedIds?.includes(id) ?? false;
            const inActiveLib = this.activeLibraryId !== "default" &&
                this.activeLibraryPhonemes.some((p) => p.id === id);
            const isCustom = this.customPhonemes.some((p) => p.id === id) || inActiveLib;
            const isPreset = this.activeLibraryId === "default" && LANES.includes(id);
            const capturing = (s?.calibrating ?? false) && s?.calibratingId === id;
            const prog = capturing
                ? Math.round((s?.calibrateProgress ?? 0) * 100)
                : 0;
            const busy = s?.calibrating ?? false;
            const btnLabel = isTap
                ? "Tap = Pop!"
                : capturing
                    ? `Listening…`
                    : tuned
                        ? "Recalibrate"
                        : "Calibrate";
            const hint = isTap
                ? `<div style="font-size:11px;opacity:.6;margin-top:2px">
                     Use the Pop Panel to record several pops in a guided,
                     rhythmic way.
                   </div>`
                : "";
            const meta = this.phonemeProps(id, isPreset);
            const infoBlock = meta.description || meta.pronunciation
                ? `<div style="font-size:11px;line-height:1.5;margin-top:4px;
                       padding:6px 8px;border-radius:8px;
                       background:rgba(34,211,238,.08)">
                     ${meta.description
                    ? `<div style="opacity:.85">${escapeHtml(meta.description)}</div>`
                    : ""}
                     ${meta.pronunciation
                    ? `<div style="opacity:.85;margin-top:${meta.description ? "4px" : "0"}">
                          <b style="color:#22d3ee">How:</b> ${escapeHtml(meta.pronunciation)}</div>`
                    : ""}
                   </div>`
                : "";
            const popPanelBtn = isTap
                ? `<button data-poppanel="${id}"
                       style="pointer-events:auto;cursor:pointer;
                       padding:6px 10px;border-radius:8px;border:1px solid #22d3ee;
                       background:#1a1f3a;color:#fff;
                       font-size:12px;font-weight:600">Pop Panel…</button>`
                : "";
            const removeBtn = isCustom || isPreset
                ? `<button data-remove-phoneme="${id}"
                        data-preset="${isPreset ? "1" : "0"}"
                        style="pointer-events:auto;cursor:pointer;
                        padding:6px 10px;border-radius:8px;border:1px solid #f87171;
                        background:#1a1f3a;color:#fff;
                        font-size:12px;font-weight:600">${isPreset ? "Delete" : "Remove"}</button>`
                : "";
            const editBtn = isCustom || isPreset
                ? `<button data-edit-phoneme="${id}"
                       data-preset="${isPreset ? "1" : "0"}"
                       style="pointer-events:auto;cursor:pointer;
                       padding:6px 10px;border-radius:8px;border:1px solid #22d3ee;
                       background:#1a1f3a;color:#fff;
                       font-size:12px;font-weight:600">Edit</button>`
                : "";
            return `<div style="display:flex;align-items:center;gap:10px;padding:8px 10px;
                    border-radius:10px;background:${active ? "rgba(91,108,255,.28)" : "rgba(18,22,42,.6)"};
                    border:1px solid ${active ? "#5b6cff" : "#2a2f4a"}" data-practice-row="${safeId}">
                    <span style="display:inline-flex;align-items:center;justify-content:center;
                        width:34px;height:34px;border-radius:50%;background:rgba(91,108,255,.25);
                        font-weight:700;font-size:18px">${safeGlyph}</span>
                    <div style="flex:1">
                      <div style="font-weight:600">${safeLabel}${tuned ? ' <span style="color:#4ade80;font-size:11px">✓ tuned</span>' : ""}</div>
                       <div data-metric="${id}" style="font-size:12px;opacity:.7">${metric}</div>
                       ${isTap ? hint : `<div style="height:5px;border-radius:3px;background:rgba(255,255,255,.12);margin-top:5px;overflow:hidden"><div data-prog="${id}" style="height:100%;width:${capturing ? prog : 0}%;background:#fbbf24"></div></div>`}
                       ${infoBlock}
                    </div>
                    <div style="display:flex;flex-direction:column;gap:6px">
                      ${isTap
                ? ""
                : `
                      <button data-calibrate="${id}" data-tap="${isTap ? "1" : "0"}"
                          style="pointer-events:auto;cursor:${busy ? "default" : "pointer"};
                          padding:6px 10px;border-radius:8px;border:1px solid #5b6cff;
                          background:${capturing ? "#fbbf24" : "#1a1f3a"};color:#fff;
                          font-size:12px;font-weight:600;opacity:${busy && !capturing ? ".5" : "1"}">
                          ${btnLabel}</button>`}
                      ${popPanelBtn}
                       ${editBtn}
                        ${removeBtn}
                    </div>
                  </div>`;
        })
            .join("");
        // A dedicated silence-calibration row (press and hold in a quiet room).
        const silenceTuned = s?.tunedIds?.includes(SILENCE) ?? false;
        const silenceCapturing = (s?.calibrating ?? false) && s?.calibratingId === SILENCE;
        const silenceBusy = s?.calibrating ?? false;
        const silenceLabel = silenceCapturing
            ? "Listening…"
            : silenceTuned
                ? "Recalibrate"
                : "Calibrate";
        const silenceRow = `
              <div style="display:flex;align-items:center;gap:10px;padding:8px 10px;
                  border-radius:10px;background:rgba(18,22,42,.6);
                  border:1px solid ${silenceCapturing ? "#fbbf24" : "#2a2f4a"};margin-top:10px">
                <span style="display:inline-flex;align-items:center;justify-content:center;
                    width:34px;height:34px;border-radius:50%;background:rgba(91,108,255,.25);
                    font-weight:700;font-size:18px">🤫</span>
                <div style="flex:1">
                  <div style="font-weight:600">silence${silenceTuned ? ' <span style="color:#4ade80;font-size:11px">✓ tuned</span>' : ""}</div>
                  <div style="font-size:12px;opacity:.7">hold in a quiet room</div>
                </div>
                <button data-calibrate="${SILENCE}" ${silenceBusy && !silenceCapturing ? "disabled" : ""}
                    style="pointer-events:auto;cursor:pointer;
                    padding:6px 10px;border-radius:8px;border:1px solid #5b6cff;
                    background:${silenceCapturing ? "#fbbf24" : "#1a1f3a"};color:#fff;
                    font-size:12px;font-weight:600;opacity:${silenceBusy && !silenceCapturing ? ".5" : "1"}">
                    ${silenceLabel}</button>
              </div>`;
        const bodies = {
            microphone: `
                  <div style="font-size:13px;margin-bottom:8px">Status: ${micStatus}</div>
                  <div style="margin:6px 0">
                    <div style="display:flex;justify-content:space-between;font-size:12px;opacity:.7">
                      <span>Input level</span>
                    </div>
                    <div style="height:8px;border-radius:4px;background:rgba(255,255,255,.12);overflow:hidden">
                      <div data-meter="level" style="height:100%;width:${Math.round((s?.level ?? 0) * 100)}%;background:#4ade80;transition:width .08s linear"></div>
                    </div>
                  </div>`,
            detection: `
                  <div style="display:flex;align-items:center;gap:12px;margin-bottom:8px">
                    <span style="display:inline-flex;align-items:center;justify-content:center;
                        width:44px;height:44px;border-radius:50%;background:rgba(91,108,255,.25);
                        font-weight:700;font-size:22px" data-det-glyph>${detectedGlyph}</span>
                    <div>
                      <div style="font-weight:700;font-size:18px" data-det-label>${detectedLabel}</div>
                      <div style="font-size:12px;opacity:.7" data-det-meta>
                        confidence ${(s ? s.confidence * 100 : 0) | 0}% ·
                        precision ${(s ? s.precision * 100 : 0) | 0}%
                      </div>
                    </div>
                  </div>
                  <div data-pitch-readout style="display:flex;flex-wrap:wrap;gap:16px;
                      margin:0 0 12px;padding:8px 12px;border-radius:10px;
                      background:rgba(18,22,42,.6);font-size:13px">
                    ${this.pitchReadoutInner(s?.features ?? null)}
                  </div>
                  ${featureBars}`,
            visualizer: `
                  <p style="opacity:.7;font-size:12px;margin:0 0 12px">
                    A live view of the raw features driving detection: the radar
                    shows the current fingerprint, the trail shows recent motion.
                    Hover a radar axis dot to identify the metric.
                  </p>
                 <div style="margin:0 0 14px">
                   <button data-open-visualizer
                       style="pointer-events:auto;cursor:pointer;padding:10px 16px;
                       border-radius:12px;border:1px solid #22d3ee;
                       background:linear-gradient(120deg,#22d3ee,#a78bfa);
                       color:#0a0d1c;font-size:14px;font-weight:800">
                       🌈 Open Wacky Full-Screen Visualizer</button>
                   <div style="opacity:.55;font-size:11px;margin-top:6px">
                     A WinAmp-style, beat-reactive light show driven by every
                     live DSP signal — just for fun.
                   </div>
                 </div>
                  <div data-viz-wrap style="position:relative">
                    <canvas data-viz
                        style="display:block;width:100%;height:260px;border-radius:12px;
                        background:radial-gradient(circle at 30% 30%,rgba(34,211,238,.06),rgba(10,12,24,.9))"></canvas>
                    <div data-viz-tip style="display:none;position:absolute;
                        pointer-events:none;z-index:5;padding:4px 8px;border-radius:8px;
                        background:rgba(10,12,24,.92);border:1px solid #2a2f4a;
                        font-size:11px;white-space:nowrap"></div>
                  </div>
                  <div data-viz-legend style="display:flex;flex-wrap:wrap;gap:10px;
                      margin-top:10px;font-size:11px;opacity:.85"></div>`,
            library: this.buildLibraryBody(),
            practice: `
                  <p style="opacity:.7;font-size:12px;margin:0 0 10px">
                     Calibrate each sound to collect labeled training data, then a
                     neural classifier trains automatically (needs ≥2 sounds). Your
                     model is saved locally and used in play.
                  </p>
                   <div data-train-status style="font-size:12px;opacity:.85;
                       margin:0 0 10px;color:#4ade80"></div>
                  <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
                    ${practice}
                  </div>
                     ${silenceRow}`,
            addsound: `
                     <div style="padding:12px;border-radius:10px;
                       background:rgba(18,22,42,.6);border:1px solid #2a2f4a">
                     <div style="font-size:13px;font-weight:700;margin-bottom:8px">
                       Add a new sound</div>
                      <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center">
                        <button type="button" data-icon-toggle
                            style="pointer-events:auto;cursor:pointer;width:52px;height:38px;
                            text-align:center;padding:6px;border-radius:8px;
                            border:1px solid #2a2f4a;background:#12162a;color:#fff;
                            font-size:18px">${this.newPhonemeIcon || "🔤"}</button>
                        <input data-new-id type="text" placeholder="id (e.g. click)"
                            style="pointer-events:auto;flex:0 0 140px;padding:6px 8px;
                            border-radius:8px;border:1px solid #2a2f4a;
                            background:#12162a;color:#fff;font-size:13px"/>
                        <input data-new-label type="text" placeholder="label (e.g. click sound)"
                            style="pointer-events:auto;flex:1;min-width:140px;padding:6px 8px;
                            border-radius:8px;border:1px solid #2a2f4a;
                            background:#12162a;color:#fff;font-size:13px"/>
                        <button data-add-phoneme
                            style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                            border-radius:8px;border:1px solid #4ade80;background:#1a1f3a;
                            color:#fff;font-size:13px;font-weight:600">Add</button>
                      </div>
                      <div data-icon-picker style="display:none;flex-wrap:wrap;gap:6px;
                          margin-top:8px;padding:8px;border-radius:8px;
                          background:#0d1024;border:1px solid #2a2f4a">
                        ${EMOJI_PALETTE.map((e) => `<button type="button" data-pick-emoji="${e}"
                                 style="pointer-events:auto;cursor:pointer;width:34px;
                                 height:34px;border-radius:8px;border:1px solid #2a2f4a;
                                 background:#12162a;font-size:18px">${e}</button>`).join("")}
                      </div>
                      <label style="display:inline-flex;align-items:center;gap:8px;
                          margin-top:10px;font-size:13px;pointer-events:auto;cursor:pointer">
                        <input type="checkbox" data-new-percussive
                            ${this.newPhonemePercussive ? "checked" : ""}
                            style="pointer-events:auto;cursor:pointer"/>
                        Percussive (tap/onset) — otherwise sustained (hold)
                      </label>
                     <label style="display:inline-flex;align-items:center;gap:8px;
                         margin-top:6px;font-size:13px;pointer-events:auto;cursor:pointer">
                       <input type="checkbox" data-new-pitchvariant
                           ${this.newPhonemePitchVariant ? "checked" : ""}
                           style="pointer-events:auto;cursor:pointer"/>
                       Use pitch to discriminate (speaker-specific; default off)
                     </label>
                     <div style="opacity:.55;font-size:11px;margin-top:6px">
                       New sounds appear above; calibrate them to collect training
                       data, then they'll be recognized in play.
                     </div>
                      <div style="margin-top:14px;padding-top:12px;
                          border-top:1px solid #2a2f4a">
                        <div style="font-size:13px;font-weight:700;margin-bottom:8px">
                          Library</div>
                        <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center">
                          <button data-lib-export
                              style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                              border-radius:8px;border:1px solid #22d3ee;background:#1a1f3a;
                              color:#fff;font-size:13px;font-weight:600">Copy to clipboard</button>
                          <button data-lib-import
                              style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                              border-radius:8px;border:1px solid #22d3ee;background:#1a1f3a;
                              color:#fff;font-size:13px;font-weight:600">Paste from clipboard</button>
                          ${this.hiddenPresets.length > 0
                ? `
                          <button data-restore-presets
                              style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                              border-radius:8px;border:1px solid #fbbf24;background:#1a1f3a;
                              color:#fff;font-size:13px;font-weight:600">
                              Restore presets (${this.hiddenPresets.length})</button>`
                : ""}
                          <span data-lib-status style="font-size:12px;font-weight:600"></span>
                        </div>
                        <div style="opacity:.55;font-size:11px;margin-top:6px">
                          Export/import your custom sounds and deleted presets as
                          JSON to back up or share an emote library.
                        </div>
                    </div>
                   </div>`,
            traindata: this.buildTrainDataBody(s),
            studio: `
                 <div style="display:flex;justify-content:flex-end;align-items:center;margin-bottom:8px">
                   <button data-gain-calibrate
                       style="pointer-events:auto;cursor:pointer;padding:6px 10px;
                       border-radius:8px;border:1px solid #4ade80;background:#1a1f3a;
                       color:#fff;font-size:12px;font-weight:600;margin-right:auto">
                       Auto-calibrate…</button>
                   <button data-dsp-reset
                       style="pointer-events:auto;cursor:pointer;padding:6px 10px;
                       border-radius:8px;border:1px solid #5b6cff;background:#1a1f3a;
                       color:#fff;font-size:12px;font-weight:600">Reset</button>
                 </div>
                 <p style="opacity:.7;font-size:12px;margin:0 0 12px">
                   Room & levels: noise tolerance, input gain, and how finely
                   audio is analyzed. Tune these to your microphone and room.
                   Changes apply live and are saved locally, or use
                   "Auto-calibrate…" for a guided bouncing-ball setup.
                 </p>
                 ${STUDIO_CONTROLS.map((c) => this.dspRow(c)).join("")}`,
            speaker: `
                 <div style="display:flex;justify-content:flex-end;align-items:center;margin-bottom:8px">
                   <button data-dsp-reset
                       style="pointer-events:auto;cursor:pointer;padding:6px 10px;
                       border-radius:8px;border:1px solid #5b6cff;background:#1a1f3a;
                       color:#fff;font-size:12px;font-weight:600">Reset</button>
                 </div>
                 <p style="opacity:.7;font-size:12px;margin:0 0 12px">
                   Speaker tuning: frequency-band selection, onset/impulse
                   cutoffs, and pitch estimation. These characterize how your
                   phonemes are spectrally distinguished. Changing these alters
                   the feature space — recapture sounds afterward.
                 </p>
                 ${SPEAKER_CONTROLS.map((c) => this.dspRow(c)).join("")}`,
            classifier: `
                 <div style="display:flex;justify-content:flex-end;align-items:center;margin-bottom:8px">
                   <button data-clf-reset
                       style="pointer-events:auto;cursor:pointer;padding:6px 10px;
                       border-radius:8px;border:1px solid #5b6cff;background:#1a1f3a;
                       color:#fff;font-size:12px;font-weight:600">Reset</button>
                 </div>
                 <p style="opacity:.7;font-size:12px;margin:0 0 12px">
                   Tune the SVM classifier used for sound recognition. Changes
                   apply on the next retrain — press "Retrain now" in Training data
                   (or recalibrate a sound) to rebuild the model with these settings.
                 </p>
                 ${CLASSIFIER_CONTROLS.map((c) => this.classifierRow(c)).join("")}`,
            system: `
                  <div style="font-size:13px;line-height:1.7">
                     Engine: <span data-engine>${store.usingWasm.get() ? "native (WASM)" : "JS fallback"}</span><br>
                    AudioWorklet: ${typeof AudioWorkletNode !== "undefined" ? "supported" : "missing"}<br>
                    WebGPU: ${"gpu" in navigator ? "available" : "unavailable"}<br>
                    Events observed: <span data-events>${s?.eventCount ?? 0}</span>
                  </div>
                   <div style="margin-top:12px">
                     <button data-open-visualizer
                         style="pointer-events:auto;cursor:pointer;padding:8px 14px;
                         border-radius:10px;border:1px solid #22d3ee;
                         background:linear-gradient(120deg,#22d3ee,#a78bfa);
                         color:#0a0d1c;font-size:13px;font-weight:800">
                         🌈 Open Wacky Visualizer</button>
                     <div style="opacity:.55;font-size:11px;margin-top:6px">
                       A full-screen, beat-reactive light show driven by every
                       live DSP signal — just for fun.
                     </div>
                   </div>`,
        };
        const sectionsHtml = this.layout.order
            .map((id) => this.sectionShell(id, bodies[id]))
            .join("");
        this.innerHTML = `
              <div data-sections style="max-width:720px;margin:0 auto;color:#fff">
                 <h1 style="margin:0 0 4px">Settings</h1>
                <p style="opacity:.8;margin:0 0 20px">
                  Test your microphone, practice each sound, and verify the audio
                  engine — no scoring here. Drag ⠿ to reorder, click a header to collapse.
                </p>
                ${sectionsHtml}
              </div>
            `;
        this.wireSectionChrome();
        // Wire calibrate buttons to a bubbling CustomEvent the shell handles.
        this.querySelectorAll("[data-calibrate]").forEach((btn) => {
            const id = btn.getAttribute("data-calibrate");
            if (!id)
                return;
            const isTap = btn.getAttribute("data-tap") === "1";
            // Transient gestures ("pop") no longer use this button — they
            // calibrate exclusively through the Pop Panel. This handler is
            // only wired for sustained (hold) gestures.
            void isTap;
            let held = false;
            const start = (ev) => {
                ev.preventDefault();
                // Guard against a stale hold from a previous (now-replaced)
                // button node: if the panel still thinks a hold is active,
                // force-release it first so we never get wedged "on".
                if (this.holdingId && this.holdingId !== id) {
                    this.dispatchEvent(new CustomEvent("calibrate-end", {
                        detail: { clusterId: this.holdingId },
                        bubbles: true,
                    }));
                    this.holdingId = null;
                }
                if (held)
                    return;
                held = true;
                this.holdingId = id;
                btn.setPointerCapture?.(ev.pointerId);
                this.dispatchEvent(new CustomEvent("calibrate-start", {
                    detail: { clusterId: id },
                    bubbles: true,
                }));
            };
            const end = (ev) => {
                ev.preventDefault();
                // Release even if our local `held` flag was lost (e.g. the
                if (!held && this.holdingId !== id)
                    return;
                held = false;
                this.holdingId = null;
                this.dispatchEvent(new CustomEvent("calibrate-end", {
                    detail: { clusterId: id },
                    bubbles: true,
                }));
            };
            btn.addEventListener("pointerdown", start);
            btn.addEventListener("pointerup", end);
            btn.addEventListener("pointercancel", end);
            btn.addEventListener("pointerleave", end);
        });
        this.querySelectorAll("[data-poppanel]").forEach((btn) => {
            const id = btn.getAttribute("data-poppanel");
            if (!id)
                return;
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                this.dispatchEvent(new CustomEvent("poppanel-open", {
                    detail: { clusterId: id },
                    bubbles: true,
                }));
            });
        });
        this.querySelector("[data-add-phoneme]")?.addEventListener("click", (e) => {
            e.preventDefault();
            const idInp = this.querySelector("[data-new-id]");
            const labelInp = this.querySelector("[data-new-label]");
            const percInp = this.querySelector("[data-new-percussive]");
            const pitchInp = this.querySelector("[data-new-pitchvariant]");
            void this.addCustomPhoneme(idInp?.value ?? "", labelInp?.value ?? "", this.newPhonemeIcon || "❓", percInp?.checked ?? false, 
            // Checkbox is "use pitch"; invariant is the inverse.
            !(pitchInp?.checked ?? false));
        });
        const iconToggle = this.querySelector("[data-icon-toggle]");
        const iconPicker = this.querySelector("[data-icon-picker]");
        iconToggle?.addEventListener("click", (e) => {
            e.preventDefault();
            if (iconPicker) {
                iconPicker.style.display =
                    iconPicker.style.display === "none" ? "flex" : "none";
            }
        });
        this.querySelectorAll("[data-pick-emoji]").forEach((btn) => {
            const emoji = btn.getAttribute("data-pick-emoji");
            if (!emoji)
                return;
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                this.newPhonemeIcon = emoji;
                if (iconToggle)
                    iconToggle.textContent = emoji;
                if (iconPicker)
                    iconPicker.style.display = "none";
            });
        });
        this.querySelector("[data-new-percussive]")?.addEventListener("change", (e) => {
            this.newPhonemePercussive = e.target.checked;
        });
        this.querySelector("[data-new-pitchvariant]")?.addEventListener("change", (e) => {
            this.newPhonemePitchVariant = e.target.checked;
        });
        this.querySelector("[data-lib-export]")?.addEventListener("click", (e) => {
            e.preventDefault();
            void this.exportLibraryToClipboard();
        });
        this.querySelector("[data-lib-import]")?.addEventListener("click", (e) => {
            e.preventDefault();
            void this.importLibraryFromClipboard();
        });
        this.querySelector("[data-restore-presets]")?.addEventListener("click", (e) => {
            e.preventDefault();
            void this.restorePresets();
        });
        this.querySelectorAll("[data-remove-phoneme]").forEach((btn) => {
            const id = btn.getAttribute("data-remove-phoneme");
            if (!id)
                return;
            const isPreset = btn.getAttribute("data-preset") === "1";
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                if (isPreset) {
                    void this.removePresetPhoneme(id);
                }
                else {
                    void this.removeCustomPhoneme(id);
                }
            });
        });
        this.querySelectorAll("[data-edit-phoneme]").forEach((btn) => {
            const id = btn.getAttribute("data-edit-phoneme");
            if (!id)
                return;
            const isPreset = btn.getAttribute("data-preset") === "1";
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                this.showEditPopup(id, isPreset);
            });
        });
        this.querySelectorAll("[data-dsp]").forEach((inp) => {
            const key = inp.getAttribute("data-dsp");
            if (!key)
                return;
            const ctrl = DSP_CONTROLS.find((c) => c.key === key);
            inp.addEventListener("input", () => {
                const val = Number(inp.value);
                void saveDspConfig({ [key]: val });
                const out = this.querySelector(`[data-dsp-val='${String(key)}']`);
                if (out && ctrl)
                    out.textContent = this.fmtDsp(ctrl, val);
                if (key === "frameHopMs") {
                    this.dispatchEvent(new CustomEvent("dsp-frame-hop", {
                        detail: { frameHopMs: val },
                        bubbles: true,
                    }));
                }
                else {
                    this.dispatchEvent(new CustomEvent("dsp-config-changed", { bubbles: true }));
                }
            });
        });
        this.querySelectorAll("[data-dsp-reset]").forEach((resetBtn) => {
            resetBtn.addEventListener("click", () => {
                void saveDspConfig({ ...DEFAULT_DSP_CONFIG });
                for (const c of DSP_CONTROLS) {
                    const inp = this.querySelector(`[data-dsp='${String(c.key)}']`);
                    const out = this.querySelector(`[data-dsp-val='${String(c.key)}']`);
                    const v = DEFAULT_DSP_CONFIG[c.key];
                    if (inp)
                        inp.value = String(v);
                    if (out)
                        out.textContent = this.fmtDsp(c, v);
                }
                this.dispatchEvent(new CustomEvent("dsp-frame-hop", {
                    detail: { frameHopMs: DEFAULT_DSP_CONFIG.frameHopMs },
                    bubbles: true,
                }));
                console.info("[ac-diagnostics] DSP config reset to defaults");
            });
        });
        this.querySelector("[data-gain-calibrate]")?.addEventListener("click", (e) => {
            e.preventDefault();
            this.dispatchEvent(new CustomEvent("gainpanel-open", { bubbles: true }));
        });
        this.querySelectorAll("[data-clf]").forEach((inp) => {
            const key = inp.getAttribute("data-clf");
            if (!key)
                return;
            const ctrl = CLASSIFIER_CONTROLS.find((c) => c.key === key);
            inp.addEventListener("input", () => {
                const val = Number(inp.value);
                store.gameSettings.update((s) => ({ ...s, [key]: val }));
                const out = this.querySelector(`[data-clf-val='${key}']`);
                if (out && ctrl)
                    out.textContent = this.fmtClassifier(ctrl, val);
                this.dispatchEvent(new CustomEvent("classifier-config-changed", {
                    detail: this.classifierPatchFromSettings(),
                    bubbles: true,
                }));
            });
            inp.addEventListener("change", () => {
                if (this.autoRetrain) {
                    console.info("[ac-diagnostics] classifier setting committed — " +
                        "auto-retraining");
                    this.dispatchEvent(new CustomEvent("train-retrain", { bubbles: true }));
                }
            });
        });
        this.querySelector("[data-clf-reset]")?.addEventListener("click", (e) => {
            e.preventDefault();
            store.gameSettings.update((s) => ({
                ...s,
                classifierC: DEFAULT_GAME_SETTINGS.classifierC,
                classifierGamma: DEFAULT_GAME_SETTINGS.classifierGamma,
                classifierRejectConfidence: DEFAULT_GAME_SETTINGS.classifierRejectConfidence,
                classifierTestFraction: DEFAULT_GAME_SETTINGS.classifierTestFraction,
                classifierImpulseCBoost: DEFAULT_GAME_SETTINGS.classifierImpulseCBoost,
            }));
            for (const c of CLASSIFIER_CONTROLS) {
                const inp = this.querySelector(`[data-clf='${c.key}']`);
                const out = this.querySelector(`[data-clf-val='${c.key}']`);
                const v = DEFAULT_GAME_SETTINGS[c.key];
                if (inp)
                    inp.value = String(v);
                if (out)
                    out.textContent = this.fmtClassifier(c, v);
            }
            this.dispatchEvent(new CustomEvent("classifier-config-changed", {
                detail: this.classifierPatchFromSettings(),
                bubbles: true,
            }));
            console.info("[ac-diagnostics] classifier config reset");
            if (this.autoRetrain) {
                this.dispatchEvent(new CustomEvent("train-retrain", { bubbles: true }));
            }
        });
        this.wireTrainDataButtons();
        this.wireLibraryButtons();
        this.setupVisualizer();
        this.querySelectorAll("[data-open-visualizer]").forEach((btn) => {
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                this.dispatchEvent(new CustomEvent("open-visualizer", { bubbles: true }));
            });
        });
        this.built = true;
        this.decorateSuggestion();
    }
    decorateSuggestion() {
        const badge = this.querySelector("[data-dsp-suggest='energyScale']");
        if (!badge)
            return;
        const suggested = store.suggestedEnergyScale.get();
        if (suggested == null) {
            badge.style.display = "none";
            return;
        }
        badge.style.display = "inline";
        badge.textContent = `(suggested ${suggested} — apply)`;
        badge.title = "Auto-measured from your mic. Click to apply.";
        badge.onclick = () => {
            const ctrl = DSP_CONTROLS.find((c) => c.key === "energyScale");
            if (!ctrl)
                return;
            void saveDspConfig({ energyScale: suggested });
            const inp = this.querySelector("[data-dsp='energyScale']");
            const out = this.querySelector("[data-dsp-val='energyScale']");
            if (inp)
                inp.value = String(suggested);
            if (out)
                out.textContent = this.fmtDsp(ctrl, suggested);
            console.info(`[ac-diagnostics] applied suggested energyScale=${suggested}`);
        };
    }
    wireSectionChrome() {
        const container = this.querySelector("[data-sections]");
        if (!container)
            return;
        this.querySelectorAll("[data-section-head]").forEach((head) => {
            head.addEventListener("click", (e) => {
                if (e.target?.hasAttribute("data-drag-handle")) {
                    return;
                }
                const id = head.getAttribute("data-section-head");
                if (!id)
                    return;
                const collapsed = !(this.layout.collapsed[id] ?? false);
                this.layout.collapsed[id] = collapsed;
                const body = this.querySelector(`[data-section-body='${id}']`);
                const caret = this.querySelector(`[data-caret='${id}']`);
                if (body)
                    body.style.display = collapsed ? "none" : "block";
                if (caret) {
                    caret.style.transform = collapsed
                        ? "rotate(-90deg)"
                        : "rotate(0deg)";
                }
                saveLayout(this.layout);
            });
        });
        let dragId = null;
        this.querySelectorAll("[data-section-head]").forEach((head) => {
            const sec = head.closest("[data-section]");
            if (!sec)
                return;
            head.addEventListener("dragstart", (e) => {
                dragId = sec.getAttribute("data-section");
                sec.style.opacity = "0.5";
                e.dataTransfer?.setData("text/plain", dragId ?? "");
                if (e.dataTransfer)
                    e.dataTransfer.effectAllowed = "move";
            });
            head.addEventListener("dragend", () => {
                sec.style.opacity = "1";
                dragId = null;
            });
            sec.addEventListener("dragover", (e) => {
                if (!dragId)
                    return;
                e.preventDefault();
                if (e.dataTransfer)
                    e.dataTransfer.dropEffect = "move";
                const targetId = sec.getAttribute("data-section");
                if (targetId && targetId !== dragId) {
                    sec.style.outline = "2px dashed #5b6cff";
                    sec.style.outlineOffset = "-2px";
                }
            });
            sec.addEventListener("dragleave", () => {
                sec.style.outline = "none";
            });
            sec.addEventListener("drop", (e) => {
                e.preventDefault();
                sec.style.outline = "none";
                const targetId = sec.getAttribute("data-section");
                if (!dragId || !targetId || dragId === targetId)
                    return;
                const order = this.layout.order.slice();
                const from = order.indexOf(dragId);
                const to = order.indexOf(targetId);
                if (from < 0 || to < 0)
                    return;
                order.splice(from, 1);
                order.splice(to, 0, dragId);
                this.layout.order = order;
                saveLayout(this.layout);
                this.built = false;
                this.render();
            });
        });
    }
    setupVisualizer() {
        const legend = this.querySelector("[data-viz-legend]");
        if (legend) {
            legend.innerHTML = FEATURE_KEYS.map((k) => {
                const hue = FEATURE_HUE[k];
                return `<span style="display:inline-flex;align-items:center;gap:6px">
                    <span style="width:10px;height:10px;border-radius:50%;
                        background:hsl(${hue},85%,60%);
                        box-shadow:0 0 8px hsla(${hue},85%,60%,.8)"></span>
                    ${FEATURE_LABELS[k]}</span>`;
            }).join("");
        }
        const canvas = this.querySelector("[data-viz]");
        if (canvas) {
            const move = (e) => {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                let found = null;
                for (const h of this.radarHits) {
                    const dx = mx - h.x;
                    const dy = my - h.y;
                    if (dx * dx + dy * dy <= (h.r + 6) * (h.r + 6)) {
                        found = { key: h.key, x: mx, y: my };
                        break;
                    }
                }
                this.vizHover = found;
                this.updateVizTooltip();
            };
            canvas.addEventListener("mousemove", move);
            canvas.addEventListener("mouseleave", () => {
                this.vizHover = null;
                this.updateVizTooltip();
            });
        }
        cancelAnimationFrame(this.vizRaf);
        const loop = () => {
            this.drawVisualizer();
            this.vizRaf = requestAnimationFrame(loop);
        };
        this.vizRaf = requestAnimationFrame(loop);
    }
    updateVizTooltip() {
        const tip = this.querySelector("[data-viz-tip]");
        const wrap = this.querySelector("[data-viz-wrap]");
        if (!tip || !wrap)
            return;
        if (!this.vizHover) {
            tip.style.display = "none";
            return;
        }
        const k = this.vizHover.key;
        const v = this.last?.features?.[k] ?? this.smooth[k];
        const hue = FEATURE_HUE[k];
        tip.style.display = "block";
        tip.style.left = `${this.vizHover.x + 12}px`;
        tip.style.top = `${this.vizHover.y + 12}px`;
        tip.style.borderColor = `hsl(${hue},85%,60%)`;
        tip.innerHTML =
            `<span style="color:hsl(${hue},90%,70%);font-weight:700">` +
                `${FEATURE_LABELS[k]}</span> ` +
                `<b>${(v ?? 0).toFixed(3)}</b>`;
    }
    drawVisualizer() {
        const canvas = this.querySelector("[data-viz]");
        if (!canvas || !canvas.isConnected)
            return;
        if (this.layout.collapsed["visualizer"])
            return;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const cw = canvas.clientWidth || 600;
        const ch = canvas.clientHeight || 260;
        if (canvas.width !== Math.floor(cw * dpr) ||
            canvas.height !== Math.floor(ch * dpr)) {
            canvas.width = Math.floor(cw * dpr);
            canvas.height = Math.floor(ch * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cw, ch);
        this.phase += 0.02;
        const f = this.last?.features ?? this.smooth;
        for (const k of FEATURE_KEYS) {
            this.smooth[k] += (f[k] - this.smooth[k]) * 0.18;
        }
        const radarW = Math.min(cw * 0.42, ch);
        this.radarHits = [];
        this.drawRadar(ctx, radarW / 2 + 12, ch / 2, radarW / 2 - 30);
        const pitchH = 46;
        this.drawHistory(ctx, radarW + 28, 14, cw - radarW - 44, ch - 28 - pitchH);
        this.drawPitchPanel(ctx, radarW + 28, ch - pitchH, cw - radarW - 44, pitchH - 8);
    }
    drawRadar(ctx, cx, cy, radius) {
        const keys = RADAR_KEYS;
        const n = keys.length;
        ctx.lineWidth = 1;
        for (let ring = 1; ring <= 4; ring++) {
            const r = (radius * ring) / 4;
            ctx.strokeStyle = `rgba(255,255,255,${0.05 + ring * 0.015})`;
            ctx.beginPath();
            for (let i = 0; i <= n; i++) {
                const a = (i / n) * Math.PI * 2 - Math.PI / 2;
                const x = cx + Math.cos(a) * r;
                const y = cy + Math.sin(a) * r;
                i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.stroke();
        }
        for (let i = 0; i < n; i++) {
            const a = (i / n) * Math.PI * 2 - Math.PI / 2;
            const key = keys[i];
            const hue = FEATURE_HUE[key];
            ctx.strokeStyle = "rgba(255,255,255,0.08)";
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
            ctx.stroke();
            const dotX = cx + Math.cos(a) * (radius + 10);
            const dotY = cy + Math.sin(a) * (radius + 10);
            const hovered = this.vizHover?.key === key;
            ctx.fillStyle = `hsla(${hue},85%,60%,0.9)`;
            ctx.beginPath();
            ctx.arc(dotX, dotY, hovered ? 5 : 3.5, 0, Math.PI * 2);
            ctx.fill();
            this.radarHits.push({ x: dotX, y: dotY, r: 8, key });
        }
        const pts = [];
        for (let i = 0; i < n; i++) {
            const k = keys[i];
            const v = Math.max(0, Math.min(1, this.smooth[k]));
            const a = (i / n) * Math.PI * 2 - Math.PI / 2;
            pts.push({
                x: cx + Math.cos(a) * radius * v,
                y: cy + Math.sin(a) * radius * v,
                hue: FEATURE_HUE[k],
                v,
            });
        }
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0, "rgba(91,108,255,0.35)");
        grad.addColorStop(1, "rgba(34,211,238,0.12)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.55)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        for (const p of pts) {
            ctx.fillStyle = `hsl(${p.hue},90%,65%)`;
            ctx.shadowColor = `hsl(${p.hue},90%,60%)`;
            ctx.shadowBlur = 8 * p.v;
            ctx.beginPath();
            ctx.arc(p.x, p.y, 3 + p.v * 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
        }
        const e = Math.max(0, Math.min(1, this.smooth.energy));
        const pulse = radius * 0.16 * (0.7 + 0.3 * Math.sin(this.phase * 2));
        ctx.fillStyle = `hsla(140,85%,60%,${0.15 + e * 0.5})`;
        ctx.beginPath();
        ctx.arc(cx, cy, pulse * (0.5 + e), 0, Math.PI * 2);
        ctx.fill();
    }
    drawHistory(ctx, x, y, w, h) {
        if (w <= 0 || h <= 0)
            return;
        this.history.push({ ...this.smooth });
        const MAX = 180;
        if (this.history.length > MAX) {
            this.history.splice(0, this.history.length - MAX);
        }
        ctx.fillStyle = "rgba(255,255,255,0.03)";
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = "rgba(255,255,255,0.06)";
        ctx.lineWidth = 1;
        for (let g = 1; g < 4; g++) {
            const gy = y + (h * g) / 4;
            ctx.beginPath();
            ctx.moveTo(x, gy);
            ctx.lineTo(x + w, gy);
            ctx.stroke();
        }
        const len = this.history.length;
        if (len < 2)
            return;
        const step = w / (MAX - 1);
        for (const k of RADAR_KEYS) {
            const hue = FEATURE_HUE[k];
            ctx.beginPath();
            for (let i = 0; i < len; i++) {
                const v = Math.max(0, Math.min(1, this.history[i][k]));
                const px = x + (MAX - len + i) * step;
                const py = y + h - v * h;
                i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
            }
            ctx.strokeStyle = `hsla(${hue},85%,62%,0.9)`;
            ctx.lineWidth = 2;
            ctx.shadowColor = `hsla(${hue},85%,60%,0.6)`;
            ctx.shadowBlur = 6;
            ctx.stroke();
            ctx.shadowBlur = 0;
            const lv = Math.max(0, Math.min(1, this.history[len - 1][k]));
            const lx = x + (MAX - 1) * step;
            const ly = y + h - lv * h;
            ctx.fillStyle = `hsl(${hue},90%,65%)`;
            ctx.beginPath();
            ctx.arc(lx, ly, 2.5, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    drawPitchPanel(ctx, x, y, w, h) {
        if (w <= 0 || h <= 0)
            return;
        const f = this.last?.features ?? this.smooth;
        ctx.fillStyle = "rgba(255,255,255,0.03)";
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = "rgba(255,255,255,0.08)";
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, w, h);
        const hz = f.pitchHz ?? 0;
        const conf = f.pitchConf ?? 0;
        const voiced = hz > 0;
        const t = this.meterValue("pitchHz", hz);
        const barX = x + 8;
        const barY = y + h * 0.5;
        const barW = w - 16;
        ctx.strokeStyle = "rgba(255,255,255,0.12)";
        ctx.beginPath();
        ctx.moveTo(barX, barY);
        ctx.lineTo(barX + barW, barY);
        ctx.stroke();
        if (voiced) {
            const px = barX + barW * t;
            const hue = FEATURE_HUE.pitchHz;
            ctx.fillStyle = `hsla(${hue},90%,65%,${0.4 + conf * 0.6})`;
            ctx.beginPath();
            ctx.arc(px, barY, 4 + conf * 4, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        ctx.font = "600 11px system-ui, sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(voiced
            ? `Pitch ${Math.round(hz)}Hz  (log axis, clarity ${(conf * 100) | 0}%)`
            : "Pitch — (unvoiced)", barX, y + 14);
        ctx.textAlign = "left";
    }
    patch() {
        const s = this.last;
        if (!s)
            return;
        const theme = "child";
        const detected = s.clusterId ?? null;
        const detLabelEl = this.querySelector("[data-det-label]");
        const detGlyphEl = this.querySelector("[data-det-glyph]");
        const detMetaEl = this.querySelector("[data-det-meta]");
        if (detLabelEl) {
            detLabelEl.textContent = detected ? labelFor(detected, theme) : "—";
        }
        if (detGlyphEl) {
            detGlyphEl.textContent = detected ? iconFor(detected, theme) : "•";
        }
        if (detMetaEl) {
            detMetaEl.textContent =
                `confidence ${(s.confidence * 100) | 0}% · ` +
                    `precision ${(s.precision * 100) | 0}%`;
        }
        const pitchEl = this.querySelector("[data-pitch-readout]");
        if (pitchEl) {
            pitchEl.innerHTML = this.pitchReadoutInner(s.features);
        }
        const setMeter = (sel, v) => {
            const bar = this.querySelector(sel);
            if (bar) {
                bar.style.width = `${Math.round(Math.max(0, Math.min(1, v)) * 100)}%`;
            }
        };
        setMeter("[data-meter='level']", s.level);
        Object.keys(FEATURE_LABELS).forEach((k) => setMeter(`[data-meter='${String(k)}']`, this.meterValue(k, s.features[k] ?? 0)));
        const evEl = this.querySelector("[data-events]");
        if (evEl)
            evEl.textContent = String(s.eventCount);
        const tdBody = this.querySelector("[data-section-body='traindata']");
        if (tdBody &&
            !document.querySelector("[data-rows-popup]") &&
            this.holdingId === null) {
            const sig = JSON.stringify(Object.fromEntries(Object.entries(s.trainData ?? {}).map(([k, v]) => [k, v.length])));
            if (sig !== this.trainDataSig) {
                this.trainDataSig = sig;
                tdBody.innerHTML = this.buildTrainDataBody(s);
                this.wireTrainDataButtons();
            }
        }
        const trainEl = this.querySelector("[data-train-status]");
        if (trainEl) {
            const counts = s.trainCounts ?? {};
            const parts = Object.keys(counts)
                .sort()
                .map((l) => `${l}: ${counts[l]}`);
            trainEl.textContent = s.training
                ? "Training classifier…"
                : parts.length
                    ? `Training data — ${parts.join("  ·  ")}`
                    : "No training data yet — calibrate a sound to begin.";
        }
        const engineEl = this.querySelector("[data-engine]");
        if (engineEl) {
            engineEl.textContent = store.usingWasm.get()
                ? "native (WASM)"
                : "JS fallback";
        }
        for (const id of [...this.practiceIds(), SILENCE]) {
            const held = s.holdMs?.[id] ?? 0;
            const count = s.hits?.[id] ?? 0;
            const metricEl = this.querySelector(`[data-metric='${id}']`);
            if (metricEl && id !== SILENCE) {
                metricEl.textContent = this.isPercussive(id)
                    ? `${count} pop${count === 1 ? "" : "s"}`
                    : `held ${(held / 1000).toFixed(1)}s`;
            }
            const capturing = (s.calibrating ?? false) && s.calibratingId === id;
            const progEl = this.querySelector(`[data-prog='${id}']`);
            if (progEl) {
                progEl.style.width = capturing
                    ? `${Math.round((s.calibrateProgress ?? 0) * 100)}%`
                    : "0%";
            }
            const btn = this.querySelector(`[data-calibrate='${id}']`);
            if (btn) {
                const isTap = btn.getAttribute("data-tap") === "1";
                const tuned = s.tunedIds?.includes(id) ?? false;
                if (isTap) {
                    btn.textContent = "Tap = Pop!";
                }
                else {
                    btn.textContent = capturing
                        ? "Listening…"
                        : tuned
                            ? "Recalibrate"
                            : "Calibrate";
                }
                const busy = s.calibrating ?? false;
                btn.disabled = !isTap && busy && !capturing && this.holdingId !== id;
            }
            const row = this.querySelector(`[data-practice-row='${id}']`);
            if (row) {
                const active = detected === id;
                row.style.background = active
                    ? "rgba(91,108,255,.28)"
                    : "rgba(18,22,42,.6)";
                row.style.border = `1px solid ${active ? "#5b6cff" : "#2a2f4a"}`;
            }
        }
    }
}
Object.defineProperty(AcDiagnostics, "NOTE_NAMES", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: [
        "C",
        "C#",
        "D",
        "D#",
        "E",
        "F",
        "F#",
        "G",
        "G#",
        "A",
        "A#",
        "B",
    ]
});
customElements.define("ac-diagnostics", AcDiagnostics);
